/**
 * One item of the board, drawn as a plain element at its place in board units (the layer
 * around it pans and zooms them all), as its kind says it is (`kinds.board`): a sticky note, free
 * text, a shape, a picture from the project's files, a line drawn by hand, a GUI pack's element
 * (drawn by the pack's `Body`: UI/UX's button, input, instance...), or a card for any other kind.
 * Its look is its `style` (`shared/elements.ts`; a picture's `crop` is the part of it shown); a
 * double click writes its words in place (`Editable`), sent when the field is left; on an
 * element made of parts (an instance), a part's own words (its element's `partAt`).
 */
import { Match, Show, Switch, createMemo, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Node } from "@protocol/Node";
import { useApp } from "../../../app/context";
import { pictureOf } from "../../../shared/assets";
import { showsWords, type BoardLook, type Style } from "../../../shared/elements";
import { inkPoints, parseCrop, smoothPath } from "../../../state/board";
import type { Run } from "../../../shared/richText";
import { Editable } from "./Editable";
import { placeCss, styleCss } from "./look";

/**
 * A line drawn by hand (`style.path`, `stroke`, `width`), stretched with its box. Only its stroke
 * catches the pointer, through a wider see-through copy (`[data-drawing]`, what the eraser looks for).
 */
function Drawing(props: { id: string; place: BoardPlace; style: Record<string, string> }): JSX.Element {
  const d = createMemo(() => smoothPath(inkPoints(props.style.path ?? "", props.place.w, props.place.h)));
  const width = () => Math.max(0.5, Number(props.style.width) || 4);
  return (
    <svg viewBox={`0 0 ${props.place.w} ${props.place.h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={d()} fill="none" stroke="transparent" stroke-width={Math.max(width() * 2, 12)} stroke-linecap="round" stroke-linejoin="round" data-drawing={props.id} />
      <path d={d()} fill="none" stroke={props.style.stroke || "#1d1b26"} stroke-width={width()} stroke-linecap="round" stroke-linejoin="round" data-drawing={props.id} />
    </svg>
  );
}

/** A picture of the project's files, or its name while there is none; `crop`: the part shown (`style.crop`). */
function Picture(props: { attachments: readonly string[] | undefined; name: string; fit?: string; crop?: string }): JSX.Element {
  const { assets } = useApp();
  const src = createMemo(() => {
    const path = pictureOf(props.attachments);
    return path ? assets.url(path) : null;
  });
  return (
    <Show when={src()} fallback={<div class="board-picture-missing">{props.name}</div>}>
      {(s) => {
        const part = () => parseCrop(props.crop);
        // Cropped: the whole picture, bigger than the item, moved so the part kept fills it.
        const css = (): JSX.CSSProperties => {
          const c = part();
          return c
            ? { position: "absolute", "max-width": "none", width: `${100 / c.w}%`, height: `${100 / c.h}%`, left: `${(-c.x / c.w) * 100}%`, top: `${(-c.y / c.h) * 100}%`, "object-fit": "fill" }
            : { "object-fit": props.fit === "contain" ? "contain" : "cover" };
        };
        return <img src={s()} alt={props.name} draggable={false} style={css()} />;
      }}
    </Show>
  );
}

/** An item's inside when its words are not written here: a picture, a GUI pack's element body, else its words. */
function ElementBody(props: { look: BoardLook; text: string; style: Style; attachments?: readonly string[] }): JSX.Element {
  return (
    <Switch fallback={<Show when={showsWords(props.look)}>{props.text}</Show>}>
      <Match when={props.look.element === "image"}>
        <Picture attachments={props.attachments} name={props.text} fit={props.style.fit} />
      </Match>
      <Match when={props.look.def.Body}>{(Body) => <Dynamic component={Body()} look={props.look} text={props.text} style={props.style} attachments={props.attachments} Words={Words} Inner={ElementBody} />}</Match>
    </Switch>
  );
}

/** Words written in place, as a GUI pack's element writes a part's own. */
function Words(props: { text: string; editing: boolean; done: () => void; write: (text: string) => void }): JSX.Element {
  return <Editable text={props.text} editing={props.editing} done={props.done} write={props.write} />;
}

/** One item. `editing`: its words are being written (`editingPart`: a part's); `done` ends it. */
export function BoardItem(props: { id: string; node: Node; place: BoardPlace; selected: boolean; editing: boolean; editingPart?: string | null; done: () => void; editAt?: { x: number; y: number } | null; hidden?: boolean }): JSX.Element {
  const { board, kinds, packs } = useApp();
  const look = () => kinds.board(props.node.kind);
  const shape = () => look().element;
  const style = () => props.node.style ?? {};
  const css = createMemo((): JSX.CSSProperties => ({ ...placeCss(props.place), ...styleCss(look().def, style()) }));
  /** The classes the board mode in use gives it. */
  const modeClasses = () => packs.modeDef()?.itemClass?.(props.node) ?? {};
  const write = (text: string) => board.setText(props.id, text);
  /** Sends words with looks of their own (`style.rich`). */
  const writeRich = (runs: readonly Run[], cleared: string[]) => void board.setRich(props.id, runs, cleared);
  return (
    <div
      class={`board-item board-${shape()}`}
      classList={{ "crop-hidden": !!props.hidden, selected: props.selected, [`shape-${style().shape ?? "rectangle"}`]: shape() === "shape", ...modeClasses() }}
      data-item={props.id}
      data-kind={props.node.kind}
      style={css()}
      title={look().def.wordless && shape() !== "drawing" ? props.node.name : undefined}
    >
      <Switch>
        <Match when={shape() === "image"}>
          <Picture attachments={props.node.attachments} name={props.node.name} fit={style().fit} crop={style().crop} />
        </Match>
        <Match when={shape() === "drawing"}>
          <Drawing id={props.id} place={props.place} style={style()} />
        </Match>
        <Match when={shape() === "card"}>
          <div class="board-card-kind">{kinds.ofNode(props.node).label}</div>
          <Editable text={props.node.name} editing={props.editing} done={props.done} write={write} at={props.editAt} id={props.id} rich={style().rich} whole={style()} writeRich={writeRich} />
        </Match>
        <Match when={look().def.wordless && look().def.Body}>
          {(Body) => (
            <Dynamic
              component={Body()}
              look={look()}
              text={props.node.name}
              style={style()}
              attachments={props.node.attachments}
              item={{ id: props.id, node: props.node, place: props.place, editingPart: props.editingPart ?? null, done: props.done }}
              Words={Words}
              Inner={ElementBody}
            />
          )}
        </Match>
        <Match when={props.editing}>
          <Editable text={props.node.name} editing={true} done={props.done} write={write} at={props.editAt} id={props.id} rich={style().rich} whole={style()} writeRich={writeRich} />
        </Match>
        <Match when={shape() === "note" || shape() === "text" || (shape() === "shape" && showsWords(look()))}>
          <Editable text={props.node.name} editing={false} done={props.done} write={write} id={props.id} rich={style().rich} whole={style()} writeRich={writeRich} />
          <Show when={shape() === "note" && props.node.note}>
            <div class="board-note-more">{props.node.note}</div>
          </Show>
        </Match>
        <Match when={true}>
          <ElementBody look={look()} text={props.node.name} style={style()} attachments={props.node.attachments} />
        </Match>
      </Switch>
      <Show when={props.node.comments.length}>
        <span class="board-comments" title={`${props.node.comments.length} comment(s): the inspector shows them`}>
          {props.node.comments.length}
        </span>
      </Show>
    </div>
  );
}
